/**
 * Sprint V103 (Mo 21.09.2026) — Konto-Löschen-Modal (DSGVO Art. 17).
 *
 * Doppelte-Bestätigung-Pattern:
 *  1. Preview: was wird gelöscht (via GET /api/account/deletion-preview)
 *  2. User muss LÖSCHEN in Grossbuchstaben eintippen
 *  3. Confirm-Button feuert DELETE /api/account/self
 *  4. Nach Erfolg: Redirect zu /gelöscht (Bestätigungsseite)
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CONFIRM_TEXT = "LÖSCHEN";

export default function DeleteAccountModal({ open, onClose }) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [confirmInput, setConfirmInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    if (!open) {
      setConfirmInput("");
      setDeleteError(null);
      return;
    }
    // Load preview
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    api.get("/account/deletion-preview")
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((e) => {
        if (!cancelled) {
          setPreviewError("Vorschau konnte nicht geladen werden. Du kannst trotzdem löschen.");
        }
      });
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const canConfirm = confirmInput.trim().toUpperCase() === CONFIRM_TEXT && !deleting;

  const handleDelete = async () => {
    if (!canConfirm) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete("/account/self");
      // Auth-Cookies wurden serverseitig geleert; setUser(false) statt null damit
      // AuthContext das als "explicit logout" verbucht
      try { setUser?.(false); } catch { /* ignore */ }
      navigate("/gelöscht", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Sprint V102.b — 409 Conflict = Pro-User Guard hat gegriffen (sollte
      // eigentlich nie hier landen weil Frontend das UI schon versteckt, aber
      // Race-Condition-safe).
      if (status === 409 && typeof detail === "object") {
        setDeleteError(
          `${detail.message || "Konto kann nicht gelöscht werden"} ` +
          (detail.cancel_url ? `— ${detail.cancel_url}` : ""),
        );
      } else {
        const detailStr = typeof detail === "string" ? detail : (detail?.message || err?.message || "unbekannter Fehler");
        setDeleteError(`Löschung fehlgeschlagen: ${detailStr}`);
      }
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="delete-account-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 border-2 border-red-500/50 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-6 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-red-600 dark:text-red-400">
                Konto endgültig löschen
              </p>
              <h2 className="mt-0.5 text-xl font-extrabold text-slate-900 dark:text-white">
                Bist du sicher?
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-account-close-btn"
            aria-label="Schließen"
            className="rounded-lg text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 p-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="space-y-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
            <p><b className="text-slate-900 dark:text-white">Dieser Schritt kann nicht rückgängig gemacht werden.</b></p>
            <ul className="space-y-1.5 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold mt-0.5">&bull;</span>
                <span>Alle deine Daten (Flexpfad-Historie, QuickCheck-Ergebnis, Reflexionen, Einstellungen) werden dauerhaft gelöscht.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold mt-0.5">&bull;</span>
                <span>Du bekommst keine E-Mails mehr von uns (Nurture-Impulse, Webinar-Reminder, Video-Drip werden gestoppt).</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 font-bold mt-0.5">&bull;</span>
                <span>Dein Zugang zum PocketCoach endet sofort nach der Bestätigung.</span>
              </li>
            </ul>
          </div>

          {/* Preview */}
          {preview && preview.total_items > 0 && (
            <div
              className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-600 dark:text-slate-300"
              data-testid="delete-account-preview"
            >
              <p className="font-bold text-slate-700 dark:text-slate-200 mb-1">
                Übersicht: {preview.total_items} Datenpunkt{preview.total_items !== 1 ? "e" : ""} werden entfernt.
              </p>
              <p className="text-slate-500 dark:text-slate-400">
                Konto-E-Mail: <span className="font-mono">{preview.email}</span>
              </p>
            </div>
          )}
          {previewError && (
            <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="delete-account-preview-error">
              {previewError}
            </p>
          )}

          {/* Doppelte Bestätigung */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-slate-600 dark:text-slate-300 mb-2">
              Tippe <span className="font-mono text-red-600 dark:text-red-400">{CONFIRM_TEXT}</span> ein, um zu bestätigen
            </label>
            <input
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              data-testid="delete-account-confirm-input"
              className="w-full rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-lg font-mono tracking-wider text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder={CONFIRM_TEXT}
            />
          </div>

          {deleteError && (
            <p className="text-sm text-red-600 dark:text-red-400 font-semibold" data-testid="delete-account-error">
              {deleteError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            data-testid="delete-account-cancel-btn"
            className="rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-sm px-4 py-2"
          >
            Doch nicht löschen
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={!canConfirm}
            data-testid="delete-account-confirm-btn"
            className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm px-4 py-2"
          >
            {deleting
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Lösche …</>
              : <><Trash2 className="w-4 h-4" /> Konto endgültig löschen</>}
          </button>
        </div>
      </div>
    </div>
  );
}
